/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

 /* tslint:disable:max-line-length */

import {
  Collection,
  FulfillmentService,
  ImageTag,
  Navigation,
  NavigationType,
  Product,
  ProductVariant,
} from '@/models';

import { Slide } from './sections/Carousel.vue';

const products: Product[] = [
  new Product({
    id: '1',
    name: '美國 OSTER Blend Active 隨我型果汁機',
    images: [
      {
        id: '1',
        tag: ImageTag.MainImage,
        handle: 'https://image-cdn.qdm.cloud/site/q597b0bed3df23/image/cache/data/2018/07/20/95529e3e3ef8478d82f18116380e23dc-max-w-1024.jpg',
      },
    ],
    priceCurrency: 'TWD',
    variants: [
      {
        id: '1',
        price: '1580',
      },
    ],
  }),

  new Product({
    id: '2',
    name: 'OZEN 真空抗氧化破壁食物調理機',
    images: [
      {
        id: '2',
        tag: ImageTag.MainImage,
        handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2018/07/25/b1ae991c7f918d8e25dcb95e24f308ea-max-w-1024.jpg',
      },
    ],
    priceCurrency: 'TWD',
    variants: [
      {
        id: '2',
        price: '19800',
      },
    ],
  }),

  new Product({
    id: '3',
    name: '美國 Drinkmate Rhino 410 (犀牛機) 氣泡水機',
    images: [
      {
        id: '3',
        tag: ImageTag.MainImage,
        handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2018/08/01/0773aa506267b2500c3290c70e46c7a0-max-w-1024.jpg',
      },
    ],
    priceCurrency: 'TWD',
    variants: [
      {
        id: '3',
        price: '2980',
      },
    ],
  }),

  new Product({
    id: '18',
    name: ' Asus 華碩 VivoBook Max X541NA - 15.6" HD - Pentium N4200 - 4GB - 500GB HDD',

    featureBullets: [
      '配備 Intel Pentium N4200 四核心處理器、15.6" HD LED 鏡面寬螢幕 (1366 x 768)',
      '4GB DDR 記憶體、500GB 硬碟',
      '配備 USB 3.1 Type-C 及藍芽 4.0',
      '超輕盈 1.8 kg',
      '隨機安裝 Windows 10 Home 作業系統',
    ],
    brand: 'Asus 華碩',
    priceCurrency: 'TWD',
    options: [
      {
        id: '1',
        name: '顏色',
      },
    ],
    variants: [
      {
        id: '100',
        options: ['黑色'],
        images: [
          { id: 50, tag: ImageTag.MainImage },
          { id: 51, tag: ImageTag.ProductAlt1Image },
          { id: 52, tag: ImageTag.ProductAlt2Image },
          { id: 53, tag: ImageTag.ProductAlt3Image },
          { id: 54, tag: ImageTag.ProductAlt4Image },
          { id: 55, tag: ImageTag.ProductAlt5Image },
          { id: 56, tag: ImageTag.ProductAlt6Image },
          { id: 57, tag: ImageTag.ProductAlt7Image },
          { id: 58, tag: ImageTag.ProductAlt8Image },
          { id: 59, tag: ImageTag.ProductAlt9Image },
        ].map(({ id, tag }) => ({
          id: `100${id.toString()}`,
          tag,
          handle: `https://picsum.photos/600/600/?image=${id}`,
        })),
        price: '13900',
        compareAtPrice: '15900',
        inventory: {
          id: '1001',
          quantity: 4,
          metadata: {
            restockDate: new Date(),
          },
        },
      },
      {
        id: '200',
        options: ['銀色'],
        images: [
          { id: 60, tag: ImageTag.MainImage },
          { id: 61, tag: ImageTag.ProductAlt1Image },
          { id: 62, tag: ImageTag.ProductAlt2Image },
          { id: 63, tag: ImageTag.ProductAlt3Image },
          { id: 64, tag: ImageTag.ProductAlt4Image },
          { id: 65, tag: ImageTag.ProductAlt5Image },
          { id: 66, tag: ImageTag.ProductAlt6Image },
          { id: 67, tag: ImageTag.ProductAlt7Image },
          { id: 68, tag: ImageTag.ProductAlt8Image },
          { id: 69, tag: ImageTag.ProductAlt9Image },
        ].map(({ id, tag }) => ({
          id: `200${id.toString()}`,
          tag,
          handle: `https://picsum.photos/600/600/?image=${id}`,
        })),
        price: '13900',
        compareAtPrice: '15900',
        inventory: {
          id: '1001',
          quantity: 10,
          metadata: {
            restockDate: new Date(),
          },
        },
      },
    ],
  }),
];

const menu: Navigation[] = [
  {
    id: '101',
    name: '2018 涼夏商品',
    type: NavigationType.Collection,
  },
  {
    id: '102',
    name: '防空汙抗敏專區',
    type: NavigationType.Collection,
  },
  {
    id: '5',
    name: '3C 數位',
    type: NavigationType.Collection,
    children: [
      {
        id: '501',
        name: '相機',
        type: NavigationType.Collection,
      },
      {
        id: '502',
        name: '手機',
        type: NavigationType.Collection,
      },
      {
        id: '503',
        name: '耳機',
        type: NavigationType.Collection,
      },
      {
        id: '504',
        name: '穿戴 / 智慧錶',
        type: NavigationType.Collection,
      },
    ],
  },
  {
    id: '1',
    name: '家電',
    type: NavigationType.Collection,
    children: [
      {
        id: '110',
        name: '清淨除濕',
        type: NavigationType.Collection,
        children: [
          {
            id: '140',
            name: '空氣清淨機',
            type: NavigationType.Collection,
          },
          {
            id: '111',
            name: '吸塵器與耗材',
            type: NavigationType.Collection,
          },
          {
            id: '112',
            name: '掃地機器人',
            type: NavigationType.Collection,
          },
        ],
      },
      {
        id: '120',
        name: '廚房電器',
        type: NavigationType.Collection,
        children: [
          {
            id: '121',
            name: '電鍋/電子鍋',
            type: NavigationType.Collection,
          },
          {
            id: '122',
            name: '烤箱',
            type: NavigationType.Collection,
          },
          {
            id: '123',
            name: '微波爐',
            type: NavigationType.Collection,
          },
          {
            id: '124',
            name: '電熱水瓶',
            type: NavigationType.Collection,
          },
          {
            id: '125',
            name: '電磁爐',
            type: NavigationType.Collection,
          },
          {
            id: '126',
            name: '調理機',
            type: NavigationType.Collection,
          },
          {
            id: '127',
            name: '咖啡機',
            type: NavigationType.Collection,
          },
        ],
      },
      {
        id: '130',
        name: '電視',
        type: NavigationType.Collection,
      },
      {
        id: '140',
        name: '洗/乾衣機',
        type: NavigationType.Collection,
      },
      {
        id: '150',
        name: '電風扇',
        type: NavigationType.Collection,
      },
      {
        id: '160',
        name: '冰箱',
        type: NavigationType.Collection,
      },
      {
        id: '170',
        name: '美容家電',
        type: NavigationType.Collection,
      },
    ],
  },
  {
    id: '2',
    name: '居家生活',
    type: NavigationType.Collection,
    children: [
      {
        id: '210',
        name: '餐廚鍋具',
        type: NavigationType.Collection,
        children: [
          {
            id: '211',
            name: '平底鍋',
            type: NavigationType.Collection,
          },
          {
            id: '212',
            name: '鑄鐵鍋',
            type: NavigationType.Collection,
          },
          {
            id: '213',
            name: '不沾鍋',
            type: NavigationType.Collection,
          },
          {
            id: '214',
            name: '炒鍋',
            type: NavigationType.Collection,
          },
          {
            id: '215',
            name: '壓力鍋',
            type: NavigationType.Collection,
          },
          {
            id: '216',
            name: '調理鍋',
            type: NavigationType.Collection,
          },
          {
            id: '217',
            name: '烤爐/烤盤',
            type: NavigationType.Collection,
          },
          {
            id: '218',
            name: '鬆餅機',
            type: NavigationType.Collection,
          },
          {
            id: '219',
            name: '保鮮容器',
            type: NavigationType.Collection,
          },
        ],
      },
      {
        id: '220',
        name: '寢具枕類',
        type: NavigationType.Collection,
      },
      {
        id: '230',
        name: '寵物用品',
        type: NavigationType.Collection,
      },
      {
        id: '240',
        name: '居家服務',
        type: NavigationType.Collection,
      },
    ],
  },
  {
    id: '3',
    name: '日用雜貨',
    type: NavigationType.Collection,
    children: [
      {
        id: '310',
        name: '除臭清潔劑',
        type: NavigationType.Collection,
      },
      {
        id: '320',
        name: '掃除用具',
        type: NavigationType.Collection,
      },
      {
        id: '330',
        name: '防蟲膏劑',
        type: NavigationType.Collection,
      },
      {
        id: '340',
        name: '地墊',
        type: NavigationType.Collection,
      },
      {
        id: '350',
        name: '金紙',
        type: NavigationType.Collection,
      },
    ],
  },
  {
    id: '4',
    name: '運動戶外',
    type: NavigationType.Collection,
    children: [
      {
        id: '410',
        name: '露營用具',
        type: NavigationType.Collection,
      },
      {
        id: '420',
        name: '健身器材',
        type: NavigationType.Collection,
      },
      {
        id: '430',
        name: '登山車',
        type: NavigationType.Collection,
      },
      {
        id: '440',
        name: '車用電子',
        type: NavigationType.Collection,
      },
      {
        id: '450',
        name: '雨衣雨鞋',
        type: NavigationType.Collection,
      },
    ],
  },
];

// Products in the cart
const cart: ProductVariant[] = products.slice(0, 2).map((product) => product.variants[0]);

// Slides displayed on Home page
const homeSlides: Slide[] = [
  {
    id: '1',
    imageSrc: 'https://hanssem-ozen.com.sg/sites/default/files/Video%20Background%203.jpg',
    imageAlt: 'OZEN 真空抗氧化破壁食物調理機',
    link: '#',
  },
  {
    id: '2',
    imageSrc: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/91ef310840118c26403c81a98d85fe33.jpg',
    imageAlt: '玉山銀行卡友優惠',
    link: '#',
  },
  {
    id: '3',
    imageSrc: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/data/2018/07/20/8caeae048d72ef7bcd4738a94c5e63d7.jpg',
    imageAlt: 'Hitachi 家電 2018 仲夏時尚禮',
    link: '#',
  },
];

// Featured collections displayed on Home page
const homeCollections: Collection[] = [
  {
    id: '1',
    name: '廚房電器',
    image: require('@/assets/home-featured-collection-1.jpg'),
  },
  {
    id: '2',
    name: '防空污抗敏',
    image: require('@/assets/home-featured-collection-2.jpg'),
  },
  {
    id: '3',
    name: '夏季抗暑',
    image: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2018/06/06/4f925dece0d267f9bb18577cca91de52-max-w-1024.jpg',
  },
];

const homeFeaturedCollections: Collection[] = [
  {
    id: '4',
    name: '超夯新品',
    products: [
      new Product({
        id: '4',
        name: '雅潔不銹鋼加底笛音壺',
        images: [
          {
            id: '4',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2018/08/17/786528d8e080eaf2481cc7ae3f6036fb-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '4',
            price: '1480',
          },
        ],
      }),
      new Product({
        id: '5',
        name: '雅登歐式雙耳平鍋',
        images: [
          {
            id: '5',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn.qdm.cloud/site/q597b0bed3df23/image/cache/data/2018/08/17/b4eccb6602801d458487097f04a4f67b-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '5',
            price: '3200',
          },
        ],
      }),
      new Product({
        id: '6',
        name: '原石活動式把手不沾鍋五件組',
        images: [
          {
            id: '6',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2018/08/17/0effc72167b177368189922f9bb11cf0-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '6',
            price: '3000',
          },
        ],
      }),
      new Product({
        id: '7',
        name: '3M 雙效空氣清淨除濕機',
        images: [
          {
            id: '7',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2018/07/25/730a07b753a1fcc9007ca9138c9caa0e-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '7',
            price: '9880',
          },
        ],
      }),
      new Product({
        id: '8',
        name: '艾美特 14 吋 DC 充電式遙控立扇',
        images: [
          {
            id: '8',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2017/08/17/e01f18a03daa65dd78561daf2c23c520-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '8',
            price: '2599',
          },
        ],
      }),
      new Product({
        id: '9',
        name: '台灣三洋 3-5 坪窗型高效能冷氣',
        images: [
          {
            id: '9',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn.qdm.cloud/site/q597b0bed3df23/image/cache/data/2017/08/01/44a028c0b195dcd7789b83bc5cb2f4eb-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '9',
            price: '15780',
          },
        ],
      }),
    ],
  },
  {
    id: '5',
    name: '暢銷商品',
    products: [
      new Product({
        id: '10',
        name: 'Panasonic 32L 變頻式微波爐',
        images: [
          {
            id: '10',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn.qdm.cloud/site/q597b0bed3df23/image/cache/data/2017/08/04/fff68dec8931f0eb00db6715dc6a7a34-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '10',
            price: '4480',
          },
        ],
      }),
      new Product({
        id: '11',
        name: '大同 11 人份全不鏽鋼電鍋',
        images: [
          {
            id: '11',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2018/07/25/2dc2a313bcb2fd67144afa3f59be1999-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '11',
            price: '3500',
          },
        ],
      }),
      new Product({
        id: '12',
        name: '大同 6 人份多功能星球電鍋',
        images: [
          {
            id: '12',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn.qdm.cloud/site/q597b0bed3df23/image/cache/data/2017/09/06/5bf042d2c416a6175351996aaf14173a-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '12',
            price: '3680',
          },
        ],
      }),
      new Product({
        id: '13',
        name: '晶工牌 8L 全開水溫熱開飲機',
        images: [
          {
            id: '13',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn.qdm.cloud/site/q597b0bed3df23/image/cache/data/2018/01/03/826c0182751f5a50374b725c1a0f0471-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '13',
            price: '1670',
          },
        ],
      }),
      new Product({
        id: '14',
        name: '喜特麗懸掛式 90C 臭氧型鏡面玻璃 ST 筷架烘碗機',
        images: [
          {
            id: '14',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2017/09/14/4eb971fe243fccf3ada4974fe7918443-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '14',
            price: '1670',
          },
        ],
      }),
      new Product({
        id: '15',
        name: '大家源 8L 經典溫控電烤箱',
        images: [
          {
            id: '15',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2017/09/15/d7a1709c1d2c4d1f30dd2dfad2445074-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '15',
            price: '799',
          },
        ],
      }),
      new Product({
        id: '16',
        name: '喜特麗渦輪增壓斜背式排油煙機 70cm',
        images: [
          {
            id: '16',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2017/08/08/4f8d988da99edadcf3dbc73845f49df8-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '16',
            price: '4480',
          },
        ],
      }),
      new Product({
        id: '17',
        name: 'Panasonic 14 吋高級型 DC 直流風扇',
        images: [
          {
            id: '17',
            tag: ImageTag.MainImage,
            handle: 'https://image-cdn-flare.qdm.cloud/q597b0bed3df23/image/cache/data/2017/09/14/f040e1711a1cdab337d585542906d413-max-w-1024.jpg',
          },
        ],
        priceCurrency: 'TWD',
        variants: [
          {
            id: '17',
            price: '2990',
          },
        ],
      }),
    ],
  },
];

const brands = [
  {
    name: 'SANLUX 台灣三洋',
    logo: require('@/assets/brand-1.png'),
  },
  {
    name: 'TECO 東元家電',
    logo: require('@/assets/brand-2.png'),
  },
  {
    name: 'Toshiba 東芝',
    logo: require('@/assets/brand-3.svg'),
  },
  {
    name: 'HITACHI 日立',
    logo: require('@/assets/brand-4.jpg'),
  },
  {
    name: 'TIGER 虎牌',
    logo: require('@/assets/brand-5.png'),
  },
  {
    name: 'ZOJIRUSHI 象印',
    logo: require('@/assets/brand-6.png'),
  },
  {
    name: 'TATUNG 大同',
    logo: require('@/assets/brand-7.svg'),
  },
  {
    name: 'Philips 飛利浦',
    logo: require('@/assets/brand-8.svg'),
  },
  {
    name: 'Panasonic 國際牌',
    logo: require('@/assets/brand-9.svg'),
  },
  {
    name: 'SAMPO 聲寶',
    logo: require('@/assets/brand-10.svg'),
  },
];

const homeInstagramPhotos = [
  {
    shortcode: 'BqbYhMZgf35',
    title: '.\n🔥限時限量🔥\n🔸三菱10.5L清淨除濕機🔸\nMJ-E105EF\n✨新會員再送100元折價卷\n✨全館滿2000免運\n✨活動只到1/15截止，售完就沒囉！\n💥紅外線感應溫差\n❄️⛄️❄️⛄️❄️⛄️❄️\n#cheaper #buy #shoppingtime #shoppingonline #onlineshopping #momo #friday #gohappy #yahoo #easypeasy #food #foodie #foodies #foodporn #rakuten #shopee #lotte #蝦皮 #松果 #雙喬 #雙喬嚴選 #漢神 #露天 #露天拍賣 #樂天 #大同 #家電',
    thumbnail: 'https://instagram.ftpe7-4.fna.fbcdn.net/vp/d60d59a39c0a9fcc29d914afb97a8745/5C7C26B4/t51.2885-15/sh0.08/e35/s640x640/42003866_2121646124569909_5525648020377778078_n.jpg',
  },
  {
    shortcode: 'BqPKlsVgMwI',
    title: '讓雙喬陪你一起對抗空污！\n快點加入line@ 抽獎吧！\n每10人 就有1人中獎哦！ 😍',
    thumbnail: 'https://instagram.ftpe7-4.fna.fbcdn.net/vp/bfbb2fb872122eeb54b4f45f6e547d2d/5C8BDA38/t51.2885-15/sh0.08/e35/s640x640/44545017_715408322157427_5932530795960063149_n.jpg',
  },
  {
    shortcode: 'BqHdowrnNJO',
    title: '抽獎 來唷來唷！！\n立刻加入LINE@：＠ twins99\n🎁喬編三不五時就免費送禮物唷！',
    thumbnail: 'https://instagram.ftpe7-4.fna.fbcdn.net/vp/d041ea1a5f21c3858ecf9a102e5e8f16/5C73A616/t51.2885-15/sh0.08/e35/s640x640/43709212_976789685842033_3340771869776918877_n.jpg',
  },
  {
    shortcode: 'BqEcwEqHi_Z',
    title: '逼逼逼~整個大爆倉🙀💥\ufeff\n小編理解你恨不得今晚就收到商品🎁\ufeff\n但是~但是~再給我們一點點時間🙏\ufeff\n遇到送貨司機不妨跟他說聲謝謝吧！😉\ufeff\n－\ufeff\n#雙11購物熱潮 喝杯 #蜂蜜檸檬 休息一下吧！',
    thumbnail: 'https://instagram.ftpe7-4.fna.fbcdn.net/vp/bf09dfc5ec8dd3b3d4482ff66dde0f66/5C756F5E/t51.2885-15/e35/44774044_2412301839059096_4001358334801908695_n.jpg',
  },
];

export default {
  products,
  cart,
  menu,
  homeSlides,
  homeCollections,
  homeFeaturedCollections,
  brands,
  homeInstagramPhotos,
};
